import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { BlockEditorProps, BlockEditorRef, GutenbergBlock } from '../types';
import { useBlockManagement } from '../hooks/useBlockManagement';
import { useContentEditable } from '../hooks/useContentEditable';
import { parseWordPressContent, serializeBlocksToWordPress } from '../utils/blockSerializer';
import { BlockEdit } from './BlockEdit';
import { BlockInserter } from './BlockInserter';

// TitleInput component
interface TitleInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const TitleInput: React.FC<TitleInputProps> = ({ value, onChange, placeholder = "Add title" }) => {
  const { ref, onInput, onKeyDown } = useContentEditable({
    value,
    onChange,
  });

  return (
    <div 
      className="editor-visual-editor__post-title-wrapper edit-post-visual-editor__post-title-wrapper has-global-padding"
      contentEditable={false}
      style={{
        marginTop: '4rem',
        paddingRight: 'var(--wp--style--root--padding-right, 0)',
        paddingLeft: 'var(--wp--style--root--padding-left, 0)',
        outline: 0,
        boxSizing: 'inherit',
        WebkitUserModify: 'read-only'
      }}
    >
      <h1
        ref={ref}
        contentEditable={true}
        className="wp-block wp-block-post-title block-editor-block-list__block editor-post-title editor-post-title__input rich-text is-selected"
        aria-label="Add title"
        role="textbox"
        aria-multiline="true"
        style={{
          whiteSpace: 'pre-wrap',
          minWidth: '1px',
          color: 'var(--wp--preset--color--contrast, #000)',
          fontSize: 'var(--wp--preset--font-size--x-large, 2.25rem)',
          fontStyle: 'normal',
          wordBreak: 'break-word',
          maxWidth: '650px',
          marginLeft: 'auto',
          marginRight: 'auto',
          outline: 0,
          boxSizing: 'inherit',
          WebkitUserModify: 'read-write'
        }}
        onInput={onInput}
        onKeyDown={onKeyDown}
        suppressContentEditableWarning={true}
        data-placeholder={value ? '' : placeholder}
      />
    </div>
  );
};

export const BlockEditor = forwardRef<BlockEditorRef, BlockEditorProps>(({ 
  content = '',
  blocks: initialBlocks = [],
  title: initialTitle = '',
  config = {},
  onSave,
  onChange,
  onTitleChange,
  className = '',
  style = {},
  placeholder = 'Start writing...',
  'aria-label': ariaLabel = 'Block Editor'
}, ref) => {
  const [isClient, setIsClient] = useState(false);
  const [showBlockInserter, setShowBlockInserter] = useState(false);
  const [inserterPosition, setInserterPosition] = useState<{ index: number; x: number; y: number } | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  
  // Initialize blocks from content or initialBlocks
  const [initialBlocksState] = useState<GutenbergBlock[]>(() => {
    if (initialBlocks.length > 0) {
      return initialBlocks;
    }
    if (content) {
      return parseWordPressContent(content);
    }
    return [];
  });

  const {
    blocks,
    setBlocks,
    title,
    setTitle,
    handleBlocksChange,
    handleSave,
    addBlock,
    updateBlock,
    removeBlock
  } = useBlockManagement(initialBlocksState, config, initialTitle);

  // Client-side only rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Inject theme CSS when component mounts
  useEffect(() => {
    if (!isClient) return;

    // Check if CSS is already injected
    if (document.getElementById('block-editor-theme-css')) {
      return;
    }

    // Get theme CSS from the global themes object
    const themes = (window as any).BlockEditor?.themes;
    if (!themes) {
      console.warn('Themes not available. Placeholder text may not display correctly.');
      return;
    }

    const themeName = config.theme || 'wordpress';
    const theme = themes[themeName] || themes.wordpress;
    
    // Create style element
    const style = document.createElement('style');
    style.id = 'block-editor-theme-css';
    style.textContent = theme.styles;
    
    // Inject into head
    document.head.appendChild(style);
  }, [isClient, config.theme]);

  // Handle content changes with debouncing to prevent excessive re-renders
  useEffect(() => {
    // Don't call onChange if user is actively typing
    const isTyping = document.activeElement && 
      document.activeElement.getAttribute('contenteditable') === 'true';
    
    if (isTyping) return;
    
    const timeoutId = setTimeout(() => {
      const serializedContent = serializeBlocksToWordPress(blocks);
      (onChange || (() => {}))(serializedContent, blocks);
    }, 100); // Debounce for 100ms
    
    return () => clearTimeout(timeoutId);
  }, [blocks, onChange]);

  // Handle title changes
  useEffect(() => {
    if (onTitleChange) {
      onTitleChange(title);
    }
  }, [title, onTitleChange]);

  // Handle save
  useEffect(() => {
    if (onSave && config.onSave) {
      // This will be called when save is triggered
    }
  }, [onSave, config.onSave]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getContent: () => serializeBlocksToWordPress(blocks),
    getBlocks: () => blocks,
    setContent: (newContent: string) => {
      const parsedBlocks = parseWordPressContent(newContent);
      setBlocks(parsedBlocks);
    },
    setBlocks: (newBlocks: GutenbergBlock[]) => {
      setBlocks(newBlocks);
    },
    addBlock: (blockType: string, attributes?: Record<string, any>, index?: number) => {
      addBlock(blockType, attributes, index);
    },
    updateBlock: (clientId: string, attributes: Record<string, any>) => {
      updateBlock(clientId, attributes);
    },
    removeBlock: (clientId: string) => {
      removeBlock(clientId);
    },
    focus: () => {
      const firstBlock = document.querySelector('[contenteditable="true"]') as HTMLElement;
      firstBlock?.focus();
    },
    blur: () => {
      const activeElement = document.activeElement as HTMLElement;
      activeElement?.blur();
    },
    save: () => {
      handleSave();
    }
  }), [blocks, addBlock, updateBlock, removeBlock, handleSave, setBlocks]);

  const handleAddBlock = useCallback((blockType: string, attributes: Record<string, any> = {}, index?: number) => {
    addBlock(blockType, attributes, index);
    setShowBlockInserter(false);
  }, [addBlock]);

  const handleImageClick = useCallback((x: number, y: number) => {
    setSelectedImageId(null);
    // Handle image toolbar or editing
  }, []);

  const handleDeleteBlock = useCallback((clientId: string) => {
    removeBlock(clientId);
  }, [removeBlock]);

  const handleInsertionPointClick = useCallback((index: number, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setInserterPosition({
      index,
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    setShowBlockInserter(true);
  }, []);

  if (!isClient) {
    return (
      <div 
        className={`block-editor ${className}`}
        style={style}
        aria-label={ariaLabel}
      >
        <div className="block-editor-loading">Loading editor...</div>
      </div>
    );
  }

  return (
    <div 
      className={`block-editor ${className}`}
      style={style}
      aria-label={ariaLabel}
    >
      {/* Title Input */}
      <TitleInput
        value={title}
        onChange={setTitle}
        placeholder="Add title"
      />

      {/* Blocks Container */}
      <div className="block-editor-block-list__layout">
        {blocks.length === 0 ? (
          <div 
            className="block-editor-block-list__empty-state"
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#666',
              border: '2px dashed #fff',
              borderRadius: '8px',
              margin: '20px 0'
            }}
            onClick={() => setShowBlockInserter(true)}
          >
            <p style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Start writing or add a block</p>
            <button
              type="button"
              style={{
                background: 'none',
                border: '1px solid #007cba',
                color: '#007cba',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Add Block
            </button>
          </div>
        ) : (
          blocks.map((block, index) => (
            <div key={block.clientId} className="block-editor-block-list__block-wrapper">
              {/* Insertion Point */}
              <div
                className="block-editor-block-list__insertion-point"
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0';
                }}
                style={{
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  margin: '4px 0',
                  opacity: 0,
                  transition: 'opacity 0.2s ease'
                }}
                onClick={(e) => handleInsertionPointClick(index, e)}
              >
                <div style={{
                  width: '100%',
                  height: '2px',
                  backgroundColor: '#007cba',
                  borderRadius: '1px',
                  position: 'relative'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInsertionPointClick(index, e);
                    }}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#007cba',
                      borderRadius: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 170, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0000ff';
                      e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.2)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 170, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#007cba';
                      e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 170, 0.3)';
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Block */}
              <div className="block-editor-block-list__block">
                <BlockEdit
                  attributes={block.attributes}
                  setAttributes={(newAttributes) => updateBlock(block.clientId, newAttributes)}
                  blockName={block.name}
                  clientId={block.clientId}
                  onImageClick={handleImageClick}
                  onDeleteBlock={handleDeleteBlock}
                />
              </div>
            </div>
          ))
        )}

        {/* Bottom Insertion Point */}
        <div
          className="block-editor-block-list__insertion-point"
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6';
          }}
          style={{
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            margin: '4px 0',
            opacity: 0.6,
            transition: 'opacity 0.2s ease'
          }}
          onClick={(e) => handleInsertionPointClick(blocks.length, e)}
        >
          <div style={{
            width: '100%',
            height: '2px',
            backgroundColor: '#007cba',
            borderRadius: '1px',
            position: 'relative'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleInsertionPointClick(blocks.length, e);
              }}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '20px',
                height: '20px',
                backgroundColor: '#007cba',
                borderRadius: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 170, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0000ff';
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.2)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 170, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#007cba';
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 170, 0.3)';
              }}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Block Inserter */}
      {showBlockInserter && (
        <BlockInserter
          isOpen={showBlockInserter}
          onClose={() => setShowBlockInserter(false)}
          onAddBlock={handleAddBlock}
          position={inserterPosition}
        />
      )}
    </div>
  );
});

BlockEditor.displayName = 'BlockEditor';
